/**
 * Public Blog listing — premium light theme, connects to /api/blogs/* backend.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock,
  Eye,
  Search,
  Tag,
  Calendar,
  ArrowRight,
  Sparkles,
  BookOpen,
  RefreshCw,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api/blogs`;

export default function PublicBlogPage() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    fetchBlogs();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedCategory, searchQuery]);

  useEffect(() => {
    fetchCategories();
    fetchTags();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("status", "published");
      params.append("limit", "24");
      if (selectedCategory) params.append("category", selectedCategory);
      if (searchQuery) params.append("search", searchQuery);
      const res = await axios.get(`${API}/list?${params.toString()}`);
      setBlogs(res.data.blogs || []);
    } catch (err) {
      toast.error("Could not load blog posts");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data.categories || []);
    } catch {}
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API}/tags`);
      setTags(res.data.tags || []);
    } catch {}
  };

  const [featured, ...rest] = blogs;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <PremiumNav />

      {/* Hero */}
      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#A855F7]/10 border border-[#A855F7]/25 text-xs uppercase tracking-widest text-[#7C3AED] font-semibold mb-5">
            <Sparkles className="w-3 h-3" /> MarkeeMix Blog
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#111111] tracking-tight max-w-3xl mx-auto">
            Guides, playbooks & product notes for teachers, creators & marketers.
          </h1>
          <p className="text-base sm:text-lg text-neutral-600 mt-5 max-w-2xl mx-auto">
            Practical how-tos on AI-powered SEO, YouTube growth, teaching workflows,
            and honest reviews of the tools we ship every week.
          </p>

          <div className="mt-8 max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search articles by keyword…"
              className="pl-12 h-12 bg-white border-neutral-300 shadow-sm"
              data-testid="blog-search"
            />
          </div>
        </div>
      </section>

      {/* Category chips */}
      {categories.length > 0 && (
        <div className="border-b border-neutral-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap gap-2 items-center">
            <button
              onClick={() => setSelectedCategory("")}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                !selectedCategory
                  ? "bg-[#111111] text-white"
                  : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
              }`}
              data-testid="cat-all"
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.name}
                onClick={() => setSelectedCategory(c.name)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  selectedCategory === c.name
                    ? "bg-[#A855F7] text-white"
                    : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                }`}
                data-testid={`cat-${c.name}`}
              >
                {c.name} <span className="opacity-60 ml-1">({c.count})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-12">
        {loading ? (
          <div className="flex justify-center py-24">
            <RefreshCw className="w-8 h-8 animate-spin text-[#A855F7]" />
          </div>
        ) : blogs.length === 0 ? (
          <Card className="p-16 text-center border-neutral-200">
            <BookOpen className="w-10 h-10 text-neutral-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-[#111111]">No posts yet.</h3>
            <p className="text-neutral-500 text-sm mt-2 max-w-md mx-auto">
              We're publishing fresh guides every week. Try a different search or check back soon.
            </p>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-[1fr_320px] gap-10">
            {/* Posts */}
            <div className="space-y-10">
              {/* Featured (first post) */}
              {featured && (
                <Card
                  onClick={() => navigate(`/blogs/${featured.slug}`)}
                  className="overflow-hidden cursor-pointer border-neutral-200 hover:shadow-lg transition-shadow group"
                  data-testid={`blog-card-${featured.id || featured.slug}`}
                >
                  <div className="grid md:grid-cols-[1.2fr_1fr]">
                    {featured.featured_image ? (
                      <img
                        src={featured.featured_image}
                        alt={featured.title}
                        className="w-full h-72 md:h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      />
                    ) : (
                      <div className="h-72 md:h-full bg-gradient-to-br from-[#A855F7] to-[#7C3AED] flex items-center justify-center">
                        <BookOpen className="w-14 h-14 text-white/80" />
                      </div>
                    )}
                    <div className="p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className="bg-[#A855F7]/10 text-[#7C3AED] hover:bg-[#A855F7]/15 border-transparent">
                          {featured.category || "Latest"}
                        </Badge>
                        <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">
                          Featured
                        </span>
                      </div>
                      <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#111111] group-hover:text-[#7C3AED] transition-colors">
                        {featured.title}
                      </h2>
                      {featured.excerpt && (
                        <p className="text-sm text-neutral-600 mt-3 line-clamp-3">{featured.excerpt}</p>
                      )}
                      <div className="mt-5 flex items-center gap-4 text-xs text-neutral-500">
                        {featured.reading_time > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {featured.reading_time} min read
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(featured.published_at || featured.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                        {featured.views > 0 && (
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" /> {featured.views}
                          </span>
                        )}
                      </div>
                      <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#7C3AED]">
                        Read article <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Rest of posts */}
              {rest.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-6">
                  {rest.map((b) => (
                    <Card
                      key={b.id || b.slug}
                      onClick={() => navigate(`/blogs/${b.slug}`)}
                      className="overflow-hidden cursor-pointer border-neutral-200 hover:shadow-md transition-shadow group"
                      data-testid={`blog-card-${b.id || b.slug}`}
                    >
                      {b.featured_image ? (
                        <img
                          src={b.featured_image}
                          alt={b.title}
                          className="w-full h-44 object-cover group-hover:scale-[1.03] transition-transform duration-500"
                        />
                      ) : (
                        <div className="h-44 bg-gradient-to-br from-neutral-100 to-neutral-200 flex items-center justify-center">
                          <BookOpen className="w-8 h-8 text-neutral-400" />
                        </div>
                      )}
                      <div className="p-5">
                        <Badge variant="secondary" className="mb-3 text-[10px]">
                          {b.category || "General"}
                        </Badge>
                        <h3 className="font-heading text-base font-semibold text-[#111111] line-clamp-2 group-hover:text-[#7C3AED] transition-colors">
                          {b.title}
                        </h3>
                        {b.excerpt && (
                          <p className="text-xs text-neutral-500 mt-2 line-clamp-2">{b.excerpt}</p>
                        )}
                        <div className="mt-4 flex items-center gap-3 text-[11px] text-neutral-500">
                          {b.reading_time > 0 && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {b.reading_time}m
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(b.published_at || b.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                          </span>
                          {b.views > 0 && (
                            <span className="flex items-center gap-1 ml-auto">
                              <Eye className="w-3 h-3" /> {b.views}
                            </span>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              {tags.length > 0 && (
                <Card className="p-5 border-neutral-200">
                  <h3 className="font-heading text-sm font-semibold text-[#111111] flex items-center gap-2 mb-3">
                    <Tag className="w-4 h-4 text-[#A855F7]" /> Popular tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {tags.slice(0, 20).map((t) => (
                      <button
                        key={t.name}
                        onClick={() => setSearchQuery(t.name)}
                        className="px-2 py-1 text-[11px] rounded-md bg-neutral-100 hover:bg-[#A855F7]/10 text-neutral-700 hover:text-[#7C3AED] transition-colors"
                      >
                        #{t.name}
                      </button>
                    ))}
                  </div>
                </Card>
              )}

              <Card className="p-6 border-neutral-200 bg-gradient-to-br from-[#A855F7] to-[#7C3AED] text-white">
                <h3 className="font-heading text-lg font-semibold mb-2">Try MarkeeMix free</h3>
                <p className="text-white/90 text-xs leading-relaxed mb-4">
                  75+ AI tools for teachers, creators & marketers — no credit card, no downloads.
                </p>
                <Button
                  className="w-full bg-white text-[#7C3AED] hover:bg-neutral-100 font-semibold"
                  onClick={() => navigate("/tools")}
                >
                  Explore tools <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>

              <Card className="p-6 border-neutral-200">
                <h3 className="font-heading text-sm font-semibold text-[#111111] mb-2">Get weekly guides</h3>
                <p className="text-xs text-neutral-500 mb-4">
                  One practical tutorial per week. No spam, unsubscribe with one click.
                </p>
                <Button
                  variant="outline"
                  className="w-full border-neutral-300"
                  onClick={() => navigate("/signup")}
                >
                  Sign up free
                </Button>
              </Card>
            </aside>
          </div>
        )}
      </main>

      <PremiumFooter />
    </div>
  );
}
